// Countdown management module for PPT Hero game
export default class CountdownManager {
  constructor(scene) {
    this.scene = scene;
  }

  // Start level timer with countdown (Rex UI only - no legacy timer)
  startLevelTimer() {
    // Clear any existing legacy timer
    if (this.scene.levelTimer) {
      this.scene.levelTimer.destroy();
      this.scene.levelTimer = null;
    }

    // Don't create legacy timer - Rex UI timer handles display
    console.log('🔧 Using Rex UI timer only - no legacy timer created');

    // FIXED: Don't override timeRemaining if it's already properly set (our safeguards)
    if (!this.scene.timeRemaining || this.scene.timeRemaining <= 0) {
      this.scene.timeRemaining = this.getLevelTimeLimit();
      console.log('🔧 CountdownManager set timeRemaining:', this.scene.timeRemaining);
    } else {
      console.log('🔧 CountdownManager keeping existing timeRemaining:', this.scene.timeRemaining);
    }
    
    // Start countdown
    this.scene.timerEvent = this.scene.time.addEvent({
      delay: 1000,
      callback: this.updateTimer,
      callbackScope: this,
      loop: true
    });
  }

  // Update timer countdown
  updateTimer() {
    if (this.scene.timeRemaining > 0) {
      this.scene.timeRemaining--;
      
      // Update Rex UI timer if available
      if (this.scene.rexUIManager) {
        const isUrgent = this.scene.timeRemaining <= 30;
        this.scene.rexUIManager.updateTimer(this.scene.timeRemaining, isUrgent);
      }
      
      this.updateTimerDisplay();
      
      // Visual feedback when time is running low
      if (this.scene.levelTimer && this.scene.timeRemaining <= 10) {
        this.scene.levelTimer.setColor('#ff0000');
        this.scene.levelTimer.setScale(1.2);
        
        // Add urgency sound or animation
        if (this.scene.timeRemaining <= 5) {
          this.scene.levelTimer.setScale(1.5);
        }
      }
    } else {
      // Time's up! - but only handle once
      if (this.scene.isHandlingTimeout) {
        return; // Already handling timeout
      }
      
      if (this.scene.timerEvent) {
        this.scene.time.removeEvent(this.scene.timerEvent);
        this.scene.timerEvent = null;
      }
      
      if (this.scene.levelTimer) {
        this.scene.levelTimer.destroy();
        this.scene.levelTimer = null;
      }
      
      // Clear Rex UI timer
      if (this.scene.rexUIManager) {
        this.scene.rexUIManager.clearTimerPanel();
      }
      
      // Handle time out
      this.handleTimeOut();
    }
  }

  // Update timer display
  updateTimerDisplay() {
    // Only update legacy timer if it exists
    if (!this.scene.levelTimer) return;
    
    const minutes = Math.floor(this.scene.timeRemaining / 60);
    const seconds = this.scene.timeRemaining % 60;
    const timeString = `${minutes}:${seconds.toString().padStart(2, '0')}`;
    
    this.scene.levelTimer.setText(timeString);
  }

  // Get time limit for current level
  getLevelTimeLimit() {
    // PRIORITY: Use scene.timeLimit if already set (this has our Bain safeguards)
    if (this.scene.timeLimit && this.scene.timeLimit > 0) {
      console.log('🔧 Using pre-set scene.timeLimit:', this.scene.timeLimit);
      return this.scene.timeLimit;
    }
    
    // Fallback: Try to find level data by ID first, not array index
    if (this.scene.levels && Array.isArray(this.scene.levels)) {
      let levelData = this.scene.levels.find(level => level.id === this.scene.currentLevel);
      if (!levelData && this.scene.currentLevel >= 0 && this.scene.currentLevel <= 10) {
        // For levels 0-10, try array index as fallback
        levelData = this.scene.levels[this.scene.currentLevel];
      }
      
      if (levelData && levelData.timeLimit) {
        return levelData.timeLimit;
      }
    }
    
    return this.getDefaultTimeLimit();
  }
  
  // Get default time limit based on current level
  getDefaultTimeLimit() {
    
    // Default time limits based on level
    if (this.scene.currentLevel === 0) return 0; // Practice mode - no timer
    if (this.scene.currentLevel <= 5) return 120; // 2 minutes for early levels
    if (this.scene.currentLevel <= 10) return 180; // 3 minutes for calibration
    if (this.scene.currentLevel <= 15) return 240; // 4 minutes for advanced
    
    // Bain levels always get 60 seconds
    if ([22, 23, 24, 25, 28, 29].includes(this.scene.currentLevel)) return 60;
    
    return 300; // 5 minutes for other expert levels
  }

  // Handle time out with improved flow
  handleTimeOut() {
    // Prevent multiple timeout calls
    if (this.scene.isHandlingTimeout) {
      console.log('⏰ Timeout already being handled, skipping');
      return;
    }
    
    console.log('⏰ Timer expired - handling timeout');
    this.scene.isHandlingTimeout = true;
    
    // Set game state to prevent further updates
    this.scene.gameState = 'transitioning';
    this.scene.inputEnabled = false;
    
    // Show timeout notification
    this.scene.showCenterNotification('⏰ Time\'s up!', 'warning', 2000);
    
    // Mark level as failed
    this.scene.levelComplete = true; // Prevent further updates
    
    // Save analytics for timeout
    if (this.scene.saveLevelAnalytics) {
      this.scene.saveLevelAnalytics(false);
    }
    
    // Clear any instructions or UI elements
    if (this.scene.clearLevelInstructions) {
      this.scene.clearLevelInstructions();
    }
    
    // Fade out shapes for smooth transition
    if (this.scene.fadeOutCurrentShapes) {
      this.scene.fadeOutCurrentShapes();
    }
    
    // Proceed to next level after brief pause
    this.scene.time.delayedCall(2500, () => {
      this.scene.nextLevelImmediate();
    });
  }
  
  // Legacy method for compatibility
  handleTimeOutLegacy() {
    this.scene.showCenterNotification('Time\'s up!', 'warning', 2000);
    
    // Mark level as failed
    this.scene.levelComplete = false;
    
    // Save analytics
    this.scene.saveLevelAnalytics(false);
    
    // Show retry option
    setTimeout(() => {
      this.scene.showCenterNotification('Press RESTART to try again', 'info', 3000);
    }, 2000);
  }

  // Pause timer
  pauseTimer() {
    if (this.scene.timerEvent) {
      this.scene.timerEvent.paused = true;
    }
  }

  // Resume timer
  resumeTimer() {
    if (this.scene.timerEvent) {
      this.scene.timerEvent.paused = false;
    }
  }

  // Stop timer
  stopTimer() {
    if (this.scene.timerEvent) {
      this.scene.time.removeEvent(this.scene.timerEvent);
      this.scene.timerEvent = null;
    }
    
    if (this.scene.levelTimer) {
      this.scene.levelTimer.destroy();
      this.scene.levelTimer = null;
    }
  }

  // Get remaining time
  getRemainingTime() {
    return this.scene.timeRemaining || 0;
  }

  // Add bonus time
  addBonusTime(seconds) {
    if (this.scene.timeRemaining !== undefined) {
      this.scene.timeRemaining += seconds;
      this.updateTimerDisplay();
      
      // Show bonus notification
      this.scene.showCenterNotification(`+${seconds}s bonus time!`, 'success', 1500);
    }
  }

  // Format time for display
  formatTime(seconds) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  // Check if timer is active
  isTimerActive() {
    return this.scene.timerEvent && !this.scene.timerEvent.paused;
  }

  // Get timer progress (0-1)
  getTimerProgress() {
    const totalTime = this.getLevelTimeLimit();
    if (totalTime === 0) return 1;
    
    const remaining = this.getRemainingTime();
    return Math.max(0, (totalTime - remaining) / totalTime);
  }

  // Professional countdown sequence
  startProfessionalCountdown() {
    this.scene.gameState = 'countdown';
    this.scene.inputEnabled = false;
    
    // Create countdown display with better styling
    this.createProfessionalCountdownDisplay();
    
    // Start countdown sequence
    this.executeProfessionalCountdown();
  }

  // Professional countdown display creation with responsive sizing
  createProfessionalCountdownDisplay() {
    console.log("Creating professional countdown display");
    if (this.scene.countdownDisplay) {
      this.scene.countdownDisplay.destroy();
    }

    // Center countdown in the white canvas area instead of full screen
    const slideArea = this.scene.slideArea;
    const centerX = slideArea ? slideArea.x : this.scene.cameras.main.width / 2;
    const centerY = slideArea ? slideArea.y : this.scene.cameras.main.height / 2;
    
    // Responsive font size based on screen dimensions
    const baseFontSize = Math.min(this.scene.cameras.main.width, this.scene.cameras.main.height) * 0.12;
    const fontSize = Math.max(60, Math.min(100, baseFontSize));

    // Create countdown with game theme styling
    this.scene.countdownDisplay = this.scene.add.text(centerX, centerY, "", {
      fontFamily: 'Arial, sans-serif',
      fontSize: `${fontSize}px`,
      fontWeight: 'bold',
      color: '#ffffff',
      stroke: '#00d4ff', // Game theme blue
      strokeThickness: Math.max(4, fontSize * 0.08),
      shadow: {
        offsetX: 0,
        offsetY: 0,
        color: '#00d4ff', // Game theme blue shadow
        blur: fontSize * 0.2,
        stroke: true,
        fill: true
      },
      backgroundColor: "rgba(26, 37, 47, 0.95)", // Slightly more opaque
      padding: { 
        x: Math.max(30, fontSize * 0.5), 
        y: Math.max(15, fontSize * 0.25) 
      }
    });
    
    this.scene.countdownDisplay.setOrigin(0.5);
    this.scene.countdownDisplay.setDepth(2000);
    this.scene.countdownDisplay.setAlpha(0); // Start invisible for smooth fade in
  }

  // Execute professional countdown sequence
  executeProfessionalCountdown() {
    const numbers = ['3', '2', '1', 'GO!'];
    let currentIndex = 0;

    // Set game state to countdown and disable input
    this.scene.gameState = 'countdown';
    this.scene.inputEnabled = false;
    console.log('🎮 Game state set to countdown');

    // Create countdown display if not exists
    if (!this.scene.countdownDisplay) {
      this.createProfessionalCountdownDisplay();
    }

    // Instructions will auto-clear after 5 seconds - don't clear them here
    console.log('📋 Instructions will auto-clear - countdown starting');

    const showNextNumber = () => {
      if (currentIndex >= numbers.length) {
        // Countdown complete, start gameplay
        this.scene.time.delayedCall(500, () => {
          this.scene.startGameplay();
        });
        return;
      }

      const number = numbers[currentIndex];

      // Set flag when "GO!" is displayed and clear instructions
      if (number === 'GO!') {
        this.scene.goMessageShown = true;
        console.log('🎯 GO! message shown - shortcuts now enabled');
        
        // Clear instructions when GO! appears
        if (this.scene.clearLevelInstructions) {
          this.scene.clearLevelInstructions();
        }
      }

      if (this.scene.countdownDisplay) {
        this.scene.countdownDisplay.setText(number);

        // Fade in and scale animation
        this.scene.tweens.add({
          targets: this.scene.countdownDisplay,
          alpha: 1,
          scaleX: { from: 0.5, to: 1.2 },
          scaleY: { from: 0.5, to: 1.2 },
          duration: 200,
          ease: "Back.easeOut",
          onComplete: () => {
            // Hold for a moment, then fade out (except for GO!)
            const holdDuration = number === 'GO!' ? 300 : 600;
            this.scene.time.delayedCall(holdDuration, () => {
              if (number === 'GO!') {
                // Special fade out for GO!
                this.scene.tweens.add({
                  targets: this.scene.countdownDisplay,
                  alpha: 0,
                  scaleX: 1.5,
                  scaleY: 1.5,
                  duration: 400,
                  ease: "Sine.easeIn",
                  onComplete: () => {
                    currentIndex++;
                    this.scene.time.delayedCall(100, showNextNumber);
                  }
                });
              } else {
                // Regular fade out for numbers
                this.scene.tweens.add({
                  targets: this.scene.countdownDisplay,
                  alpha: 0,
                  scaleX: 0.8,
                  scaleY: 0.8,
                  duration: 300,
                  ease: "Sine.easeIn",
                  onComplete: () => {
                    currentIndex++;
                    this.scene.time.delayedCall(100, showNextNumber);
                  }
                });
              }
            });
          }
        });
      }

      if (number !== 'GO!') {
        // Don't increment for GO! as it's handled in the fade out
        if (number === '3' || number === '2' || number === '1') {
          // Numbers will increment in their fade out callback
        }
      }
    };

    // Start the countdown
    showNextNumber();
  }
}
